/**
 * Shared label constants for roles, job roles, task types, and priorities.
 *
 * Centralizes all bilingual (Arabic/English) label maps so they are not
 * duplicated across component files.
 */

/**
 * System role display labels — single source of truth for any role_name
 * stored in `role_permissions.role_name` for system roles, plus the
 * portal-level AppRole enum values.
 *
 * Keyed by lower-snake_case identifier. Custom (user-defined) roles are NOT
 * present here — use {@link getRoleDisplayName} which falls back to the raw
 * name for those.
 */
export const ROLE_LABELS: Record<string, { ar: string; en: string }> = {
  // Portal-level roles (AppRole enum)
  super_admin: { ar: "مدير النظام", en: "Super Admin" },
  admin: { ar: "مدير", en: "Admin" },
  manager: { ar: "مدير فرعي", en: "Manager" },
  employee: { ar: "موظف", en: "Employee" },
  owner: { ar: "مالك", en: "Owner" },
  tenant: { ar: "مستأجر", en: "Tenant" },
  service_provider: { ar: "مزود خدمة", en: "Service Provider" },
  self_admin: { ar: "مدير ذاتي", en: "Self Admin" },
  // Job-level system roles (stored in role_permissions.role_name)
  property_owner: { ar: "مالك عقار", en: "Property Owner" },
  property_manager: { ar: "مدير عقارات", en: "Property Manager" },
  field_agent: { ar: "موظف ميداني", en: "Field Agent" },
  accountant: { ar: "محاسب", en: "Accountant" },
  default_employee: { ar: "موظف افتراضي", en: "Default Employee" },
  maintenance_staff: { ar: "فني صيانة", en: "Maintenance Staff" },
  customer_service: { ar: "خدمة عملاء", en: "Customer Service" },
  supervisor: { ar: "مشرف", en: "Supervisor" },
};

/**
 * Resolve a role identifier to its localized display name.
 *
 * For system roles (anything keyed in {@link ROLE_LABELS}) returns the
 * bilingual label. For custom user-defined role names returns the raw value
 * unchanged — those are free text and have no translation.
 */
export function getRoleDisplayName(
  roleName: string | null | undefined,
  t: (ar: string, en: string) => string,
): string {
  if (!roleName) return "";
  const key = roleName.trim().toLowerCase();
  const label = ROLE_LABELS[key];
  return label ? t(label.ar, label.en) : roleName;
}

/** Employee job-role display labels. */
export const JOB_ROLE_LABELS: Record<string, { ar: string; en: string }> = {
  admin: { ar: "مدير", en: "Admin" },
  property_manager: { ar: "مدير عقارات", en: "Property Manager" },
  accountant: { ar: "محاسب", en: "Accountant" },
  maintenance_staff: { ar: "فني صيانة", en: "Maintenance Staff" },
  customer_service: { ar: "خدمة عملاء", en: "Customer Service" },
  employee: { ar: "موظف", en: "Employee" },
};

/** Task type display labels. */
export const TASK_TYPE_LABELS: Record<string, { ar: string; en: string }> = {
  visit_request: { ar: "طلب زيارة", en: "Visit Request" },
  contract_request: { ar: "طلب تعاقد", en: "Contract Request" },
  inquiry: { ar: "استفسار", en: "Inquiry" },
  maintenance: { ar: "صيانة", en: "Maintenance" },
  // `supervision` is a distinct task_type (written by AddTaskDialog's
  // "supervision" kind). It used to collapse into `general`; it now has its own
  // value so the F10 payout classifier can tell a supervision visit apart from
  // ad-hoc general work. Display falls back gracefully for old `general` rows.
  supervision: { ar: "إشراف", en: "Supervision" },
  general: { ar: "عامة", en: "General" },
};

/* -------------------------------------------------------------------------- */
/* F10 — field-agent payout categories                                        */
/* -------------------------------------------------------------------------- */

/**
 * The CEO-confirmed payout categories. These are SEMANTIC keys — deliberately
 * decoupled from raw `tasks.task_type` and `contracts.*` so the data mapping
 * lives in one place ({@link classifyTaskPayout} + contract classification in
 * `lib/fieldAgentPayouts.ts`). A field agent is paid the configured amount per
 * matched item in each category.
 *
 * 5 named categories + a flexible `general` catch-all:
 *  - ejar        — a registered Ejar contract (contracts.ejar_number present)
 *  - brokerage   — a brokerage contract (REGA brokerage — PR #1344, not yet
 *                  shipped; included for forward-compat, 0 matches until it lands)
 *  - inspection  — an inspection visit        (task_type = "inspection")
 *  - supervision — a supervision visit        (task_type = "supervision")
 *  - viewing     — a property viewing visit   (task_type = "visit_request")
 *  - general     — ad-hoc / other work        (task_type = "general" or null).
 *                  Carries an editable note + an optional per-task amount
 *                  override so one-off work is captured and still totals.
 */
export const PAYOUT_CATEGORY_KEYS = [
  "ejar",
  "brokerage",
  "inspection",
  "supervision",
  "viewing",
  "general",
] as const;

export type PayoutCategory = (typeof PAYOUT_CATEGORY_KEYS)[number];

/** The one catch-all category that supports a note + per-task amount override. */
export const GENERAL_PAYOUT_CATEGORY: PayoutCategory = "general";

/** Categories backed by a contract (no per-agent attribution yet — see calc). */
export const CONTRACT_PAYOUT_CATEGORIES: PayoutCategory[] = ["ejar", "brokerage"];

/** Categories backed by a completed task. */
export const TASK_PAYOUT_CATEGORIES: PayoutCategory[] = [
  "inspection",
  "supervision",
  "viewing",
  "general",
];

/**
 * Bilingual labels for each payout category. Single source of truth for both
 * the admin cost editor and the per-agent payout report.
 */
export const PAYOUT_CATEGORY_LABELS: Record<PayoutCategory, { ar: string; en: string }> = {
  ejar: { ar: "إيجار", en: "Ejar" },
  brokerage: { ar: "وساطة", en: "Brokerage" },
  inspection: { ar: "معاينة", en: "Inspection" },
  supervision: { ar: "إشراف", en: "Supervision" },
  viewing: { ar: "عرض / معاينة-زيارة", en: "Viewing" },
  general: { ar: "عام / أخرى", en: "General / Other" },
};

/**
 * Map a stored `tasks.task_type` to its payout category, or `null` if the task
 * is not payout-eligible (e.g. inquiry, maintenance, contract_request).
 *
 * NOTE on the supervision/general overlap: historically the AddTaskDialog
 * "supervision" kind wrote `task_type = "general"`, so old supervision tasks are
 * indistinguishable from ad-hoc general work and fall into the `general` bucket.
 * New supervision tasks write `task_type = "supervision"` and classify correctly.
 */
export function classifyTaskPayout(taskType: string | null | undefined): PayoutCategory | null {
  switch (taskType) {
    case "inspection":
      return "inspection";
    case "supervision":
      return "supervision";
    case "visit_request":
      return "viewing";
    case "general":
    case null:
    case undefined:
      return "general";
    default:
      // inquiry, maintenance, contract_request, etc. are not payout-eligible.
      return null;
  }
}

/** Task priority display labels. */
export const PRIORITY_LABELS: Record<string, { ar: string; en: string }> = {
  low: { ar: "منخفضة", en: "Low" },
  medium: { ar: "متوسطة", en: "Medium" },
  high: { ar: "عالية", en: "High" },
  urgent: { ar: "عاجلة", en: "Urgent" },
};

/**
 * Visit interest level — bilingual labels.
 *
 * Used in three places: VisitFeedbackDialog (write), PropertyVisitsTab
 * (read), TaskDetailsModal activity timeline (read). Component-specific
 * icons/colors live in the consumer files; this map is for translations
 * only.
 */
export const INTEREST_LEVEL_LABELS: Record<
  "very_interested" | "interested" | "maybe" | "not_interested",
  { ar: string; en: string }
> = {
  very_interested: { ar: "مهتم جداً", en: "Very Interested" },
  interested: { ar: "مهتم", en: "Interested" },
  maybe: { ar: "ربما", en: "Maybe" },
  not_interested: { ar: "غير مهتم", en: "Not Interested" },
};

/**
 * Activity timeline entry kinds — bilingual labels for the chip rendered
 * next to each entry in TaskDetailsModal. Keys match the `kind` column
 * CHECK constraint on `task_activity`.
 */
export const ACTIVITY_KIND_LABELS: Record<string, { ar: string; en: string }> = {
  assignment_change: { ar: "تغيير الإسناد", en: "Assignment change" },
  note: { ar: "ملاحظة", en: "Note" },
  status_change: { ar: "تغيير الحالة", en: "Status change" },
  feedback: { ar: "تقييم", en: "Feedback" },
  reassignment: { ar: "إعادة إسناد", en: "Reassignment" },
};
